import {
  ExportOutlined,
  MinusCircleOutlined,
  UserOutlined,
  WarningTwoTone,
} from "@ant-design/icons";
import { Button } from "@farzoom/common-ui-components";
import { Button as AntButton, Col, Row, Space, Tag, Typography } from "antd";
import classNames from "classnames";
import { useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { FindPersonModal } from "./findPersonModal";
import { PERSON_ROLES_MAP, restrictedRolesToShow } from "./utils/constants";
import { PERSON_MODEL } from "./utils/personsModels";
import { useStyles } from "../styles";
import { PersonsType } from "@app/companyTab/@types/Customer";
import { notNullish } from "@utils/notNullish";
import { VerticalSpace } from "@app/UI/VerticalSpace";
import { formatDate } from "@utils/formatDate";
import { ISSUE_DATE_WARNING_MESSAGE, validatePassportIssueDate } from "@app/personInfo/utils/validatePassport";
import { PersonInfoModal } from "@app/personInfo/personInfoModal";

type CustomerPersonsType = {prefix: string};

const { Text } = Typography;

export const CustomerPersons = ({ prefix }: CustomerPersonsType) => {
  const {control, watch} = useFormContext();
  const classes = useStyles();
  const {
    fields: persons,
    remove,
    prepend,
    update,
  } = useFieldArray({ control, name: `${prefix}.persons` as "customerPersons.persons" });

  const personsW = watch(`${prefix}.persons` as "customerPersons.persons");

  const [isAdd, setIsAdd] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [tempPerson, setTempPerson] = useState<PersonsType>(PERSON_MODEL);

  const onFindPerson = () => {
    setIsAdd(true);
  };
  const onClosePerson = () => {
    setIsAdd(false);
  };

  const onAddPerson = (person: PersonsType) => {
    const index = persons.findIndex((p) => p.person.inn === person.person.inn);

    if (index === -1) {
      setTempPerson(person);
      setIsEdit(true);
      setIsAdd(false);
      return;
    }

    onEdit(index);
    setIsAdd(false);
  };

  const onPersonModalClose = () => {
    setIsEdit(false);
    setEditIndex(null);
  };
  const onEdit = (idx: number) => {
    setIsEdit(true);
    setEditIndex(idx);
  };
  const onUpdatePerson = (values: PersonsType) => {
    if (notNullish(editIndex)) {
      update(editIndex as number, values);
      onPersonModalClose();
      return;
    }

    prepend(values);
    setTempPerson(PERSON_MODEL);
    onPersonModalClose();
  };

  return (
    <div>
      <div className={classNames(classes.title, classes.titleWithAction)}>
        <span>Физлица</span>
        {/* {!config.readonly && ( */}
          <Button type="outlined" iconName="add" title="Добавить физ.лицо" onClick={onFindPerson} />
        {/* )} */}
      </div>
      <div className={classes.wrapper}>
        {persons.map(({ person, personRole, id }, idx) => (
          <Row key={id} gutter={8} align="top">
            <Col span={9}>
              <Space align="start">
                <UserOutlined style={{ fontSize: "24px" }} />
                <VerticalSpace size={0}>
                  <Text strong>
                    {person?.lastName} {person?.firstName} {person?.secondName}
                  </Text>
                  <VerticalSpace size={0}>
                    {person?.inn && <Text type="secondary">ИНН: {person?.inn}</Text>}
                    {person?.birthDate && (
                      <Text type="secondary">д.р.: {formatDate(person?.birthDate)}</Text>
                    )}
                    {person?.identityDocument?.series && (
                      <Space>
                        {!validatePassportIssueDate(
                          person?.birthDate,
                          person?.identityDocument.issuedDate
                        ) && (
                          <WarningTwoTone
                            twoToneColor="#FAAD14"
                            title={ISSUE_DATE_WARNING_MESSAGE}
                            style={{ cursor: "help" }}
                          />
                        )}
                        <Text type="secondary">
                          паспорт: {person?.identityDocument?.series}{" "}
                          {person?.identityDocument?.number}
                        </Text>
                      </Space>
                    )}
                  </VerticalSpace>
                </VerticalSpace>
              </Space>
            </Col>
            <Col xs={13} lg={14}>
              <Space size={[0, 4]} wrap>
                {personRole &&
                  Object.entries(personRole)
                    .filter(([k, value]) => !restrictedRolesToShow.includes(k) && !!value)
                    .map(([key, value]) => (
                      <Tag
                        key={key}
                        color={value ? PERSON_ROLES_MAP[key]?.color : "default"}
                        icon={!value ? <MinusCircleOutlined /> : undefined}
                      >
                        {PERSON_ROLES_MAP[key]?.name}
                      </Tag>
                    ))}
              </Space>
            </Col>
            <Col xs={2} lg={1} style={{ padding: 0, display: "flex", justifyContent: "flex-end" }}>
              {/* {!config.readonly && ( */}
                <VerticalSpace size={4}>
                  <Button
                    type="outlined"
                    iconName="edit"
                    title="Редактировать физ.лицо"
                    onClick={() => onEdit(idx)}
                  />
                  <Button
                    type="outlined"
                    iconName="remove"
                    title="Удалить физ.лицо"
                    onClick={() => remove(idx)}
                  />
                </VerticalSpace>
              {/* )} */}
              {/* {config.readonly && ( */}
                {/* <AntButton
                  title="Просмотреть карточку ФЛ"
                  icon={<ExportOutlined />}
                  onClick={() => onEdit(idx)}
                ></AntButton> */}
              {/* )} */}
            </Col>
          </Row>
        ))}
      </div>

      {isEdit && (
        <PersonInfoModal
          defaultValues={notNullish(editIndex) ? personsW[editIndex as number] : tempPerson}
          onClose={onPersonModalClose}
          onUpdate={onUpdatePerson}
          readonly={false}
        />
      )}
      {isAdd && <FindPersonModal onClose={onClosePerson} onAdd={onAddPerson} />}
    </div>
  );
};
